import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Link,
    Stack,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { LARAVEL_API_PATH, LARAVEL_API_TOKEN } from '../../constants/path';
import { 
    ConsentModalProps, 
    ConsentState, 
    ConsentEvent, 
    ConsentType,
    AvailableConsentTypesResponse,
    ConsentVersions 
} from './types';
import { setConsentVersions, REQUIRED_CONSENT_TYPES, getConsentVersions, isTestUserMode } from '../../helpers';

interface ExtendedConsentModalProps extends ConsentModalProps {
    consentTypesInfo?: AvailableConsentTypesResponse | null;
}

const ConsentModal: React.FC<ExtendedConsentModalProps> = ({ 
    open, 
    onClose, 
    patientFio,
    consentTypesInfo 
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [consents, setConsents] = useState<ConsentState>({
        data_processing_consent: false,
        data_transfer_consent: false,
        data_processing_policy_consent: false,
    });
    const [typesInfo, setTypesInfo] = useState<AvailableConsentTypesResponse | null>(
        consentTypesInfo || null
    );

    // Какие типы согласий изменились и требуют подтверждения
    const changedConsentTypes: ConsentType[] = React.useMemo(() => {
        if (!typesInfo) return [] as ConsentType[];

        const localVersions = getConsentVersions();
        const changed: ConsentType[] = [];
        REQUIRED_CONSENT_TYPES.forEach((consentType) => {
            const serverVersion = typesInfo?.types_info?.[consentType]?.text_version;
            const localVersion = localVersions[consentType as keyof ConsentVersions];
            if (!localVersion || (serverVersion && serverVersion > localVersion)) {
                changed.push(consentType);
            }
        });
        return changed;
    }, [typesInfo]);

    const fetchConsentTypes = useCallback(async () => {
        try {
            const response = await axios.get<AvailableConsentTypesResponse>(
                `${LARAVEL_API_PATH}consent-download/types/available`
            );
            setTypesInfo(response.data);
        } catch (error) {
            console.error('Error fetching consent types:', error);
            enqueueSnackbar('Ошибка при загрузке типов согласий', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    // Получаем информацию о типах согласий, если она не была передана
    useEffect(() => {
        if (open && !typesInfo) {
            void fetchConsentTypes();
        }
    }, [open, typesInfo, fetchConsentTypes]);

    // Открытие документа в новой вкладке
    const handleDocumentClick = (consentType: ConsentType) => {
        const url = `${LARAVEL_API_PATH}consent-download/${consentType}`;
        window.open(url, '_blank');
    };

    // Изменение состояния чекбокса
    const handleCheckboxChange = (consentType: keyof ConsentState) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setConsents({
            ...consents,
            [consentType]: event.target.checked,
        });
    };

    // Проверяем только те чекбоксы, которые отображаются (измененные документы)
    const allConsentsChecked = React.useMemo(() => {
        if (changedConsentTypes.length === 0) return false;
        return changedConsentTypes.every((type) => consents[type as keyof ConsentState]);
    }, [changedConsentTypes, consents]);

    // Получение IP адреса пользователя
    const getUserIP = async (): Promise<string> => {
        try {
            const response = await axios.get('https://api.ipify.org?format=json');
            return response.data.ip;
        } catch (error) {
            console.error('Error fetching IP:', error);
            return '0.0.0.0';
        }
    };

    // Генерация хеша текста согласия
    const generateTextHash = async (consentType: string): Promise<string> => {
        const text = `${consentType}_${new Date().toISOString().split('T')[0]}`;
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    };

    // Получение session_id
    const getSessionId = async (): Promise<string> => {
        // Проверяем наличие токена в localStorage
        const existingToken = localStorage.getItem('session_token');
        
        if (existingToken) {
            return existingToken;
        }
        
        try {
            // Если токена нет, получаем его с сервера
            const response = await axios.get(`${LARAVEL_API_PATH}api/token`, {
                headers: {
                    'X-API-Token': LARAVEL_API_TOKEN
                }
            });
            
            // Извлекаем токен из ответа
            if (response.data?.success && response.data?.data?.token) {
                const newToken = response.data.data.token;
                // Сохраняем токен в localStorage
                localStorage.setItem('session_token', newToken);
                return newToken;
            } else {
                throw new Error('Invalid token response');
            }
        } catch (error) {
            console.error('Error fetching session token:', error);
            throw error;
        }
    };

    // Отправка согласия
    const sendConsentEvent = async (
        consentType: ConsentType, 
        ip: string, 
        sessionId: string,
        textVersion?: number,
        name?: string
    ) => {
        const textHash = await generateTextHash(consentType);
        const event: ConsentEvent = {
            session_id: sessionId,
            source: 'website',
            consent_type: consentType,
            action: 'granted',
            consent_text_hash: textHash,
            ip: ip,
            user_agent: navigator.userAgent,
            text_version: textVersion,
            name: name,
        };

        // Если включен тестовый режим, устанавливаем user_id = -999
        if (isTestUserMode()) {
            event.user_id = -999;
        }

        await axios.post(`${LARAVEL_API_PATH}api/consent-events`, event, {
            headers: {
                'X-API-Token': LARAVEL_API_TOKEN
            }
        });
    };

    // Обработка отправки формы
    const handleSubmit = async () => {
        if (!allConsentsChecked) {
            enqueueSnackbar('Необходимо отметить все согласия', { variant: 'warning' });
            return;
        }

        setLoading(true);
        try {
            // Получаем IP и session_id
            const ip = await getUserIP();
            const sessionId = await getSessionId();

            // Получаем версии только для измененных документов
            const updatedVersions: ConsentVersions = {};

            const consentPromises = changedConsentTypes.map((consentType) => {
                const typeInfo = typesInfo?.types_info[consentType];
                const version = typeInfo?.text_version;

                if (version) {
                    updatedVersions[consentType as keyof ConsentVersions] = version;
                }

                return sendConsentEvent(consentType, ip, sessionId, version, patientFio);
            });

            await Promise.all(consentPromises);

            // Мержим новые версии только по обновленным документам с существующими
            const currentVersions = getConsentVersions();
            const mergedVersions: ConsentVersions = { ...currentVersions, ...updatedVersions };
            setConsentVersions(mergedVersions);

            enqueueSnackbar('Согласия успешно отправлены', { variant: 'success' });
            onClose();
        } catch (error: any) {
            console.error('Error sending consents:', error);
            enqueueSnackbar(
                error?.response?.data?.message || 'Ошибка при отправке согласий',
                { variant: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown
            onClose={(event, reason) => {
                // Блокируем закрытие модального окна по клику вне его или по Escape
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
        >
            <DialogTitle>
                <Typography variant="h6" component="div">
                    Для оказания услуг ООО "Доктор АРБИТАЙЛО" просьба подписать Согласие на обработку персональных данных и Согласие на передачу персональных данных, а также ознакомиться с условиями обработки персональных данных, указанными в Политике обработки персональных данных.
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {changedConsentTypes.includes('data_processing_consent') && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consents.data_processing_consent}
                                    onChange={handleCheckboxChange('data_processing_consent')}
                                    sx={{ '&.Mui-checked': { color: '#d23731' } }}
                                />
                            }
                            label={
                                <Typography>
                                    Я, {patientFio}, даю свое{' '}
                                    <Link
                                        component="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDocumentClick('data_processing_consent');
                                        }}
                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        Согласие на обработку персональных данных
                                    </Link>
                                </Typography>
                            }
                        />
                    </Box>
                    )}
                    {changedConsentTypes.includes('data_transfer_consent') && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consents.data_transfer_consent}
                                    onChange={handleCheckboxChange('data_transfer_consent')}
                                    sx={{ '&.Mui-checked': { color: '#d23731' } }}
                                />
                            }
                            label={
                                <Typography>
                                    Я, {patientFio}, даю свое{' '}
                                    <Link
                                        component="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDocumentClick('data_transfer_consent');
                                        }}
                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        Согласие на передачу персональных данных
                                    </Link>
                                </Typography>
                            }
                        />
                    </Box>
                    )}
                    {changedConsentTypes.includes('data_processing_policy_consent') && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consents.data_processing_policy_consent}
                                    onChange={handleCheckboxChange('data_processing_policy_consent')}
                                    sx={{ '&.Mui-checked': { color: '#d23731' } }}
                                />
                            }
                            label={
                                <Typography>
                                    Я, {patientFio}, подтверждаю ознакомление с{' '}
                                    <Link
                                        component="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDocumentClick('data_processing_policy_consent');
                                        }}
                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        Политикой обработки персональных данных
                                    </Link>
                                </Typography>
                            }
                        />
                    </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="secondary"
                    disabled={!allConsentsChecked || loading}
                    fullWidth
                >
                    {loading ? 'Отправка...' : 'Отправить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConsentModal;

